<script lang="ts">
	//Design system components
	import { Alert, Checkbox, Input, Text, Toggle, Button } from '@Shelly/shelly-design-system';
	//Stores
	import { saved_wifi_neworks } from 'src/stores/features/wifi/passwords';
	import { android_version, os, wifi_ssid, wifi_ssids } from 'src/stores/session/native-bridge';
	//Services
	import { format } from 'svelte-i18n';

	//Native requests
	import { get_wifi_ios } from 'src/services/native_container_requests_ios';

	//Helpers
	import {
		GEN_1_DEVICES,
		INCLUSION_HIDE_BLUETOOTH_SETTINGS,
		INCLUSION_HIDE_AP_DISABLE,
	} from 'src/constants';

	//Components
	import WiFiScannedNetworksFromGen2 from './WiFiScannedNetworksFromGen2.svelte';
	import { user_data } from 'src/stores/account/user';
	import { onMount } from 'svelte';
	import { mobile } from 'src/services/mobile-rework';
	import { capFirstChar } from 'src/helpers/utilities';
	import { nonShellyNetworks } from 'src/stores/features/inclusion';
	import WiFiScannedNetworks from './WiFiScannedNetworks.svelte';
	import compareVersions from 'compare-versions';
	import { handleOldWiFiData } from 'src/helpers/inclusion_helpers';

	export let inclusionData;
	export let isOpen = false;
	export let isScanOpen = false;

	let clearedPassword = false;
	let displayAdvancedNetworks = false;

	const handleGetWiFi = (e) => {
		inclusionData.wifi.ssid = e.detail[0][0];
		mobile.removeEventListener('get_wifi_success', handleGetWiFi);
	};

	const isIOS = $os === 'IOS';

	const errorsLabels = {
		ssid: $format('wifi_ssid_length_err_toast', {
			default: 'Not a valid SSID format. It must be between 1 and 31 characters.',
		}),
		pass: $format('wifi_pass_length_err_toast', {
			default: 'Password should be at least 8 characters long.',
		}),
	};

	onMount(() => {
		handleOldWiFiData();
		if (isIOS) {
			inclusionData.wifi.ssid =
				$saved_wifi_neworks && $saved_wifi_neworks.hasOwnProperty($user_data?.email)
					? $saved_wifi_neworks[$user_data?.email]?.ssid
					: $wifi_ssids
						? $wifi_ssids[0]
						: null;
		} else {
			inclusionData.wifi.ssid =
				$saved_wifi_neworks && $saved_wifi_neworks.hasOwnProperty($user_data?.email)
					? $saved_wifi_neworks[$user_data?.email]?.ssid
					: $wifi_ssid
						? $wifi_ssid
						: null;
		}

		if ($saved_wifi_neworks != null && $saved_wifi_neworks.hasOwnProperty($user_data?.email)) {
			//if we have filled store with data we assume that user is agreed to connect to saved wifi credentials if on first iteration he select save password
			inclusionData.wifi.pass = $saved_wifi_neworks[$user_data?.email]?.pass;
			inclusionData.wifi.savePass = true;
			inclusionData.wifi.userConfirmed = true;
			isOpen = inclusionData.wifi.pass === '';
		}
		inclusionData.notEdited =
			inclusionData.wifi.userConfirmed &&
			typeof $saved_wifi_neworks[$user_data?.email] !== 'undefined' &&
			inclusionData.wifi.pass === $saved_wifi_neworks[$user_data?.email]?.pass;

		if (
			isIOS &&
			($saved_wifi_neworks == null ||
				Object?.keys($saved_wifi_neworks)?.length <= 0 ||
				!$saved_wifi_neworks.hasOwnProperty($user_data?.email))
		) {
			get_wifi_ios();
			mobile.addEventListener('get_wifi_success', handleGetWiFi);
		}

		return () => {
			mobile.removeEventListener('get_wifi_success', handleGetWiFi);
		};
	});

	$: checked = inclusionData.wifi.savePass;

	$: disableToggleVisibility =
		$saved_wifi_neworks !== null &&
		$saved_wifi_neworks.hasOwnProperty($user_data?.email) &&
		(inclusionData.wifi.ssid === $saved_wifi_neworks[$user_data?.email]?.ssid ||
			$saved_wifi_neworks[$user_data?.email]?.ssid?.includes(inclusionData.wifi.ssid)) &&
		!clearedPassword;

	const handleWifiPassCache = () => {
		checked = !checked;
		inclusionData.notEdited = false;
		inclusionData.wifi.savePass = checked;
	};

	const toggleAdvancedNetworks = (e) => {
		if (displayAdvancedNetworks) {
			inclusionData.clearTimeout();
		} else {
			inclusionData.timeoutStart();
		}
		displayAdvancedNetworks = !displayAdvancedNetworks;
	};

	const clearToggleVisibility = () => {
		if (inclusionData?.wifi?.pass?.length === 0 && checked) {
			clearedPassword = true;
		}
	};

	let deviceForNetworkScan = { id: undefined };

	const getNetworks = () => {
		//this form will be available for gen 2 devices only, so maybe we didn't need to scan devices explicitly for gen 2 devices
		// if (GEN_1_DEVICES)
		if (inclusionData.selected.type && GEN_1_DEVICES.indexOf(inclusionData.selected.type) === -1) {
			deviceForNetworkScan = Object.assign({}, { ...inclusionData.selected });
		}
	};

	// if (inclusionData?.wifi?.ssid?.length > 0) {
	getNetworks();
	// }

	$: hasAp = !INCLUSION_HIDE_AP_DISABLE.includes(inclusionData.selected?.model);
	$: hasBluetooth = !INCLUSION_HIDE_BLUETOOTH_SETTINGS.includes(inclusionData.selected?.model);

	$: hasAfterInclusionPrompt = inclusionData?.selectVia == 'ble'; //gen 2 for now. Not possible via WiFi inclusion, because then we have inclusionData.selected as string. Maybe will be good to have a constant with gen 2 devices

	let isNativeCallAvailable = isIOS;

	const androidVersion = $android_version;

	const minVersion = '1.19.1';

	try {
		if (compareVersions.compare(androidVersion, minVersion, '>=')) {
			isNativeCallAvailable = true;
		}
	} catch (error) {
		console.error('we could not compare versions');
	}
</script>

{#if inclusionData.notEdited}
	<Alert
		class="mb-m"
		type="info"
		heading={$format('wifi_prompt_text', {
			values: { ssid: inclusionData.wifi.ssid },
		})}
	/>

	<Button
		size="m"
		variant="secondary"
		on:click={() => {
			inclusionData.notEdited = false;
			inclusionData.wifi.userConfirmed = false;
		}}
	>
		{capFirstChar(
			$format('button_edit', {
				default: 'edit',
			}),
		)}
	</Button>
{:else}
	<Text element="h2" class="mb-s">
		{$format('include_wifi_credentials', {
			default: 'Wi-Fi Credentials',
		})}
	</Text>
	<Text class="mb-s" styleType="body-small">
		{$format('inclusion_wifi_credentials_description', {
			default:
				'Please enter your personal Wi-Fi network credentials. The device will be included in this network.',
		})}
	</Text>

	{#if typeof inclusionData?.wifi !== 'undefined'}
		<Input
			id="ssid"
			aria-labelledby="ssid"
			size="l"
			class="mb-s"
			label="ssid"
			bind:value={inclusionData.wifi.ssid}
			error={inclusionData.wifi.errors.ssid}
			icon="fas fa-wifi"
			autocomplete="nope"
		/>

		<Input
			id="password"
			aria-labelledby="password"
			size="l"
			a11yProps={{
				iconButtonAriaLabel: capFirstChar($format('label_password', { default: 'password' })),
			}}
			class="mb-s"
			label={capFirstChar($format('label_password', { default: 'password' }))}
			bind:value={inclusionData.wifi.pass}
			error={!isOpen && inclusionData.wifi.errors.pass ? errorsLabels.pass : ''}
			disabled={isOpen}
			type="password"
			icon="fas fa-lock-keyhole"
			autocomplete="new-password"
			{disableToggleVisibility}
			on:input={clearToggleVisibility}
		/>
	{/if}

	<!-- {#if $user_data.test_user || $user_data.test_user_t1} -->
	{#if hasAfterInclusionPrompt && deviceForNetworkScan.id && isNativeCallAvailable}
		<WiFiScannedNetworksFromGen2
			bind:isScanOpen
			bind:inclusionData
			on:cancel={toggleAdvancedNetworks}
		/>
	{:else if $os !== 'IOS' && $nonShellyNetworks.length > 0}
		<WiFiScannedNetworks bind:inclusionData />
	{/if}
	<!-- {/if} -->

	<Checkbox
		id="check_uncheck_open_network"
		aria-labelledby="check_uncheck_open_network"
		size="m"
		bind:checked={isOpen}
		on:click={() => {
			isOpen = !isOpen;
			inclusionData.wifi.pass = isOpen ? '' : inclusionData.wifi.pass;
		}}
	>
		{$format('wifi_open_network', { default: 'Open network' })}
	</Checkbox>

	<Checkbox
		id="check_uncheck_save_password"
		aria-labelledby="check_uncheck_save_password"
		size="m"
		bind:checked
		element="h2"
		on:click={handleWifiPassCache}
		>{$format('label_save_password', {
			default: 'remember password',
		})}</Checkbox
	>
{/if}

{#if hasAfterInclusionPrompt}
	<!-- wifi ble settings component is for disabling wifi ap and bluetooth -->
	<Text element="h2" class="mb-s mt-m">
		{$format('text_title_after_inclusion', {
			default: 'After Inclusion',
		})}
	</Text>

	<Alert
		class="mb-s"
		type="info"
		isHeadingBold
		heading={$format('provision_security_info_title', {
			default: 'Security Measures',
		})}
		message={$format('provision_security_info', {
			default:
				'Due to security strengthening measures, the <b>Bluetooth</b> and <b>Access Point</b> need to be disabled after inclusion in a network. You can re-enable them in the device settings.',
		})}
	/>

	{#if hasBluetooth}
		<div class="mb-xs">
			<Toggle
				id="disable-bluetooth"
				aria-labelledby="disable-bluetooth"
				size="m"
				checked={true}
				disabled={true}
			>
				{$format('disable_bluetooth_desc', { default: 'Disable Bluetooth' })}
			</Toggle>
			<!-- <Text styleType="tiny" class="color-muted ml-xl pl-xs">
				{$format('provision_bluetooth_auto_disable_info', {
					default: 'Bluetooth RPC is automatically disabled after inclusion.',
				})}
			</Text> -->
		</div>
	{/if}

	{#if hasAp}
		<div class="mb-xs">
			<Toggle
				id="disable-wifi-ap"
				aria-labelledby="disable-wifi-ap"
				size="m"
				checked={true}
				disabled={true}
			>
				{$format('disable_wifi_ap_desc', { default: 'Disable Wi-Fi AP' })}
			</Toggle>
			<!-- <Text styleType="tiny" class="color-muted ml-xl pl-xs">
				{$format('provision_ap_auto_disable_info', {
					default: 'Wi-Fi AP is automatically disabled after inclusion.',
				})}
			</Text> -->
		</div>
	{/if}
{/if}
